import React from 'react';
import { RouteObject, Navigate } from 'react-router-dom';

import { LearningPathPageFFWrapper } from 'src/pages/LearningPathPage/LearningPathPageFFWrapper';
import {
  AlternativeCollectionType,
  AlternativeContentHubType,
  CollectionType,
  ContentHubType,
  LandingType,
} from 'src/constants';
import { App } from 'src/pages/App';
import { FeedbackPage } from 'src/pages/App/FeedbackPage';
import { ArticlePage, loader as articleLoader } from 'src/pages/ArticlePage';
import { Calendar, loader as calendarLoader } from 'src/pages/Calendar';
import { CatalogPage, loader as catalogLoader } from 'src/pages/CatalogPage';
import { MyLearningPageWrapper, loader as myLearningLoader } from 'src/pages/MyLearningPage';
import { MyLearningLayoutWrapper } from 'src/pages/MyLearningPage/MyLearningLayoutWrapper';
import { MyLearningOnDemandContent } from 'src/pages/MyLearningPage/children/MyLearningOnDemandContent';
import { LiveTrainingTab } from 'src/pages/MyLearningPage/children/LiveTrainingTab';
import { LearningEmptyState, EMPTY_STATE_TYPES } from 'src/pages/MyLearningPage/children/LearningEmptyStateV2';
import { onDemandLoader } from 'src/pages/MyLearningPage/loaders/onDemandLoader';
import { CertificationDetailPage, loader as certDetailLoader } from 'src/pages/CertificationDetailPage';
import {
  ClassPage,
  Layout as ClassPageLayout,
  classPageLoader,
  PartnersPage,
  PrivateClassPage,
  PublicClassResolver,
} from 'src/pages/ClassPages';
import {
  CertificatePreparationCollectionPage,
  loader as certPrepCollectionLoader,
} from 'src/pages/collections/CertificationPreparation';
import { loader as topicLoader, RelatedTopicCollectionPage } from 'src/pages/collections/RelatedTopicCollectionPage';
import { loader as roleLoader, RoleBasedCollectionPage } from 'src/pages/collections/RoleBasedCollectionPage';
import { CoursePageFFWrapper, loader as courseLoader } from 'src/pages/CoursePage';
import { CommunityLogin } from 'src/pages/CommunityLogin/CommunityLogin';
import { ErrorPage } from 'src/pages/ErrorPage';
import { TenantAwareHomePage } from 'src/pages/HomePage';
import { EnablementHomePage } from 'src/pages/HomePage/EnablementHomePage';
import { CertificatePreparationPage, loader as certPrepLoader } from 'src/pages/hubs/CertificatePreparationPage';
import { CertificationPage, loader as certificationLoader } from 'src/pages/hubs/CertificationPage';
import { EnablementHub, loader as enablementHubLoader } from 'src/pages/hubs/EnablementHub';
import { JiraHubPage, loader as jiraHubLoader } from 'src/pages/hubs/JiraHubPage';
import {
  LearningPathPage as LearningPathsHubPage,
  loader as learningPathsHubLoader,
} from 'src/pages/hubs/LearningPathPage';
import { DynamicHubPage, loader as dynamicHubLoader } from 'src/pages/hubs/DynamicHubPage';
import { loader as resourcesAndSupportLoader, ResourcesAndSupportPage } from 'src/pages/hubs/ResourcesAndSupportPage';
import { loader as roleBasedHubLoader, RoleBasedPage } from 'src/pages/hubs/RoleBasedPage';
import { loader as rovoHubLoader, RovoHubPage } from 'src/pages/hubs/RovoHubPage';
import { LiveTrainingPage } from 'src/pages/landings/LiveTrainingPage';
import { loader as learningPathLoader } from 'src/pages/LearningPathPage';
import { LessonPage, loader as lessonLoader } from 'src/pages/LessonPage';
import {
  getCatalogUrl,
  getCertificationUrl,
  getCollectionUrl,
  getCourseUrl,
  getHubUrl,
  getLandingUrl,
  getLessonUrl,
  getMyLearningUrl,
  getPathUrl,
  LANDING_URL_PATH,
  withPrefix,
} from 'src/utils/getUrl';

export const routes: RouteObject[] = [
  {
    path: '/',
    element: <App />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: '/',
        element: <TenantAwareHomePage />,
      },
      {
        path: '/enable',
        element: <EnablementHomePage />,
      },
      {
        path: '/enable/hub/:hubName',
        element: <EnablementHub />,
        loader: enablementHubLoader,
      },
      {
        path: '/community-login',
        element: <CommunityLogin />,
      },
      {
        path: getCatalogUrl(),
        element: <CatalogPage />,
        loader: catalogLoader,
      },
      {
        path: '/team-training/:courseId',
        element: <ClassPageLayout />,
        children: [
          {
            index: true,
            element: <PrivateClassPage />,
            loader: classPageLoader,
          },
          {
            path: 'partners',
            element: <PartnersPage />,
            loader: classPageLoader,
          },
        ],
      },
      {
        path: '/class/:courseId',
        element: <ClassPageLayout />,
        children: [
          {
            index: true,
            element: <PublicClassResolver />,
          },
          {
            path: 'session/:instanceId',
            element: <ClassPage />,
            loader: classPageLoader,
          },
        ],
      },
      {
        path: '/calendar',
        element: <Calendar />,
        loader: calendarLoader,
      },
      {
        path: getCourseUrl(':courseId'),
        element: <CoursePageFFWrapper />,
        loader: courseLoader,
      },
      {
        path: getCourseUrl(':courseId', ':pathId'),
        element: <CoursePageFFWrapper />,
        loader: courseLoader,
      },
      {
        path: getCourseUrl(':courseId', '', true),
        element: <FeedbackPage />,
      },
      {
        path: getCourseUrl(':courseId', ':pathId', true),
        element: <FeedbackPage />,
      },
      {
        path: getPathUrl(':pathId'),
        element: <LearningPathPageFFWrapper />,
        loader: learningPathLoader,
      },
      // My Learning V2 routes - nested structure
      {
        path: '/my-learning',
        element: <MyLearningLayoutWrapper />,
        children: [
          {
            index: true,
            element: <Navigate to="/my-learning/on-demand" replace />,
          },
          {
            path: 'on-demand',
            element: <MyLearningOnDemandContent />,
            loader: onDemandLoader,
            shouldRevalidate: ({ currentUrl, nextUrl }) => {
              // Only revalidate if the pathname changes, not search params (filters/sort)
              return currentUrl.pathname !== nextUrl.pathname;
            },
          },
          {
            path: 'live-training',
            element: <LiveTrainingTab />,
            // No loader - component uses useGetLiveTraining hook
          },
          {
            path: 'certifications',
            element: <LearningEmptyState emptyStateType={EMPTY_STATE_TYPES.CERTIFICATIONS_EMPTY} />,
            // No loader - empty state only
          },
        ],
      },
      // My Learning V1 legacy routes (/my-learning/in-progress, /my-learning/saved, /my-learning/complete)
      // These must come AFTER the V2 nested routes to avoid conflicts
      {
        path: getMyLearningUrl(':status'),
        element: <MyLearningPageWrapper />,
        loader: myLearningLoader,
      },
      {
        path: withPrefix(getCourseUrl(':courseId', ':pathId'), getHubUrl(ContentHubType.LEARNING_PATHS)),
        element: <CoursePageFFWrapper />,
        loader: courseLoader,
      },
      ...[
        getLessonUrl({ lessonSlug: ':lessonId' }),
        getLessonUrl({ courseSlug: ':courseId', lessonSlug: ':lessonId' }),
        getLessonUrl({ pathSlug: ':pathId', courseSlug: ':courseId', lessonSlug: ':lessonId' }),
        withPrefix(
          getLessonUrl({
            pathSlug: ':pathId',
            courseSlug: ':courseId',
            lessonSlug: ':lessonId',
          }),
          getHubUrl(ContentHubType.LEARNING_PATHS),
        ),
      ].map((path) => ({
        path,
        element: <LessonPage />,
        loader: lessonLoader,
      })),
      {
        path: withPrefix(getPathUrl(':pathId'), getHubUrl(ContentHubType.LEARNING_PATHS)),
        element: <LearningPathPageFFWrapper />,
        loader: learningPathLoader,
      },
      {
        path: getHubUrl(ContentHubType.LEARNING_PATHS),
        element: <LearningPathsHubPage />,
        loader: learningPathsHubLoader,
      },
      {
        path: getHubUrl(ContentHubType.RESOURCES_AND_SUPPORT),
        element: <ResourcesAndSupportPage />,
        loader: resourcesAndSupportLoader,
      },
      {
        path: getHubUrl(ContentHubType.RESOURCES_AND_SUPPORT, undefined, ':slug', true),
        element: <ArticlePage />,
        loader: articleLoader,
      },
      {
        path: getHubUrl(ContentHubType.ROVO),
        element: <RovoHubPage />,
        loader: rovoHubLoader,
      },
      {
        path: getHubUrl(ContentHubType.JIRA),
        element: <JiraHubPage />,
        loader: jiraHubLoader,
      },
      {
        path: '/hub/:slug',
        element: <DynamicHubPage />,
        loader: dynamicHubLoader,
      },
      {
        path: getCollectionUrl(CollectionType.TOPIC, ':topicId'),
        element: <RelatedTopicCollectionPage />,
        loader: topicLoader,
      },
      ...[
        getLandingUrl(LandingType.LIVE_TRAINING),
        withPrefix(getLandingUrl(LandingType.LIVE_TRAINING), LANDING_URL_PATH),
      ].map((path) => ({
        path,
        element: <LiveTrainingPage />,
      })),
      ...[getHubUrl(ContentHubType.CERTIFICATIONS), getCertificationUrl()].map((path) => ({
        path,
        element: <CertificationPage />,
        loader: certificationLoader,
      })),
      ...[
        withPrefix(getLandingUrl(LandingType.CERTIFICATION_DETAIL, ':certificationId'), LANDING_URL_PATH),
        getCertificationUrl(':certificationId'),
      ].map((path) => ({
        path,
        element: <CertificationDetailPage />,
        loader: certDetailLoader,
      })),
      ...[getHubUrl(AlternativeContentHubType.CERT_PREP), getHubUrl(ContentHubType.CERT_PREP)].map((path) => ({
        path,
        element: <CertificatePreparationPage />,
        loader: certPrepLoader,
      })),
      ...[getHubUrl(AlternativeContentHubType.ROLE_BASED), getHubUrl(ContentHubType.ROLE_BASED)].map((path) => ({
        path,
        element: <RoleBasedPage />,
        loader: roleBasedHubLoader,
      })),
      ...[
        getCollectionUrl(AlternativeCollectionType.CERT_PREP, ':certPrepId'),
        getCollectionUrl(CollectionType.CERT_PREP, ':certPrepId'),
      ].map((path) => ({
        path,
        element: <CertificatePreparationCollectionPage />,
        loader: certPrepCollectionLoader,
      })),
      ...[
        getCollectionUrl(AlternativeCollectionType.ROLE_BASED, ':roleId'),
        getCollectionUrl(CollectionType.ROLE_BASED, ':roleId'),
      ].map((path) => ({
        path,
        element: <RoleBasedCollectionPage />,
        loader: roleLoader,
      })),
    ],
  },
];
